
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { fetchVisibilitySettings, updateVisibilitySetting } from "@/utils/visibility";
import { useAuthSession } from "@/hooks/useAuthSession";

const MAX_RETRIES = 3;
const INITIAL_DELAY = 1000; // 1 second

export const useSectionVisibility = (contactId: string) => {
  const [sectionVisibility, setSectionVisibility] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { userId } = useAuthSession();

  const fetchVisibility = useCallback(async (retryCount = 0) => {
    try {
      if (!userId) {
        console.error("No user ID found");
        throw new Error("Authentication required");
      }

      console.log("Fetching section visibility for contact:", contactId);
      console.log("Current user:", userId);
      
      const visibilityMap = await fetchVisibilitySettings(contactId);
      
      console.log("Visibility data received:", visibilityMap);
      setSectionVisibility(visibilityMap);
      setError(null);
    } catch (err) {
      console.error("Error fetching section visibility:", err);
      
      if (retryCount < MAX_RETRIES) {
        const delay = Math.min(INITIAL_DELAY * 2 ** retryCount, 30000);
        console.log(`Retrying in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => fetchVisibility(retryCount + 1), delay);
      } else {
        setError(err as Error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load section visibility settings. Please try refreshing the page.",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [contactId, toast, userId]);

  const toggleSectionVisibility = async (sectionName: string) => {
    try {
      if (!userId) {
        throw new Error("Authentication required");
      }

      const currentVisibility = sectionVisibility[sectionName] ?? true;
      
      console.log("Toggling visibility for section:", sectionName, "to:", !currentVisibility);
      console.log("Current user:", userId);
      
      await updateVisibilitySetting(contactId, sectionName, currentVisibility);

      setSectionVisibility(prev => ({
        ...prev,
        [sectionName]: !currentVisibility
      }));

      toast({
        title: "Success",
        description: `Section visibility updated successfully`,
      });
    } catch (err) {
      console.error("Error toggling section visibility:", err);
      setError(err as Error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update section visibility",
      });
      throw err;
    }
  };

  useEffect(() => {
    if (contactId && userId) {
      fetchVisibility();
    }
  }, [contactId, fetchVisibility, userId]);

  return { 
    sectionVisibility, 
    toggleSectionVisibility, 
    loading, 
    error,
    refetch: () => fetchVisibility() 
  };
};
